import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { HandDisplay } from '../components/problems/HandDisplay';
import { AuctionTable } from '../components/problems/AuctionTable';
import { VulnerabilityIndicator } from '../components/problems/VulnerabilityIndicator';
import { ThirdLevelReviewResult } from '../components/third-level-bid-review/ThirdLevelReviewResult';
import { ThirdLevelCommentResult } from '../components/third-level-bid-review/ThirdLevelCommentResult';
import { thirdLevelBidReviewApi } from '../api/thirdLevelBidReviewApi';
import type { Problem } from '../types/problems';
import {
  DEFAULT_REVIEW_CATEGORY,
  THIRD_LEVEL_REVIEW_CATEGORIES,
  THIRD_LEVEL_REVIEW_CATEGORY_LABELS,
  type ThirdLevelBidReviewCategory,
} from '../types/thirdLevelBidReview';

const DIRECTION_NAMES: Record<'N' | 'E' | 'S' | 'W', string> = {
  N: 'North',
  E: 'East',
  S: 'South',
  W: 'West',
};

/**
 * Read-only "3rd level bids - Review" Data Editing page (feature 017). Reviews the feature-013
 * pool for a fixed subject (Jim, user id 2) in two categories, ascending by id. It mirrors the
 * "Decision to make a 3rd level bid" page layout — the same gray problem panel with the hand
 * positioned relative to the vulnerability board by its seat (`.vuln-hand-group[data-seat]`,
 * feature 016) and the same auction/result slots — but strips the quiz: no bid entry, no scoring,
 * no difficulty, no opener split. Jeff's actual call and (where present) Jim's answer are read-only.
 */
export function ThirdLevelBidReviewPage() {
  usePageTitle('3rd level bids - Review — Hawkes Bridge');

  const navigate = useNavigate();
  const [category, setCategory] = useState<ThirdLevelBidReviewCategory>(DEFAULT_REVIEW_CATEGORY);
  const [problems, setProblems] = useState<Problem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const pageScrollRef = useRef<HTMLDivElement>(null);

  const loadCategory = useCallback((next: ThirdLevelBidReviewCategory) => {
    setLoading(true);
    setError(null);
    thirdLevelBidReviewApi
      .getCandidates(next)
      .then(res => {
        setProblems(res.problems);
        setCurrentIndex(0); // FR-017: reset position when the category changes / on refresh
      })
      .catch(() => setError('Unable to load the review hands. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadCategory(category);
  }, [category, loadCategory]);

  const total = problems.length;
  const current = total > 0 ? problems[currentIndex] : null;

  // Scroll to top when the displayed hand changes.
  useEffect(() => {
    window.scrollTo?.(0, 0);
    pageScrollRef.current?.scrollTo?.(0, 0);
  }, [current?.id]);

  const atFirst = currentIndex <= 0;
  const atLast = currentIndex >= total - 1;
  const goPrev = () => !atFirst && setCurrentIndex(i => i - 1);
  const goNext = () => !atLast && setCurrentIndex(i => i + 1);

  const handleExit = () => navigate('/data-editing');
  const handleRefresh = () => loadCategory(category);

  const emptyMessage =
    category === 'incorrect-jim'
      ? 'No hands have been answered incorrectly by Jim.'
      : category === 'hands-with-comments'
        ? 'No hands have comments yet.'
        : 'No hands are available for this filter.';

  return (
    <div ref={pageScrollRef} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col">
      {/* Header: banner + position indicator + Exit (mirrors the 013 page header row) */}
      <div className="flex items-center gap-4 short:gap-2 mb-3 short:mb-2">
        <h1 className="text-lg sm:text-xl font-bold text-ink">3rd level bids - Review</h1>
        <div className="ml-auto flex items-center gap-4 short:gap-2">
          {!loading && !error && total > 0 && (
            <div className="flex items-center bg-cream border border-line rounded-lg px-3 py-1.5">
              <span className="text-sm text-ink-soft" data-testid="review-position">
                Problem: {currentIndex + 1} / {total}
              </span>
            </div>
          )}
          <button
            type="button"
            onClick={handleExit}
            className="px-3 py-1.5 text-sm font-medium text-ink-soft hover:text-ink border border-line-strong rounded-md"
          >
            Exit
          </button>
        </div>
      </div>

      {/* Data category filter + Refresh (mirrors the 013 editor toolbar row) */}
      <div className="flex items-center flex-wrap gap-2 mb-4 short:mb-2">
        <label htmlFor="review-category" className="text-sm font-bold text-ink">
          Data category:
        </label>
        <select
          id="review-category"
          value={category}
          onChange={e => setCategory(e.target.value as ThirdLevelBidReviewCategory)}
          disabled={loading}
          className="border border-line-strong rounded-md px-2 py-1 text-sm"
        >
          {THIRD_LEVEL_REVIEW_CATEGORIES.map(c => (
            <option key={c} value={c}>
              {THIRD_LEVEL_REVIEW_CATEGORY_LABELS[c]}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={loading}
          data-testid="review-refresh"
          className="px-3 py-1 text-sm font-medium rounded-md bg-cream text-ink border border-brass hover:bg-paper disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Refresh
        </button>
      </div>

      {loading && (
        <div className="flex items-center justify-center min-h-[40vh]">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {!loading && error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700" role="alert">
          {error}
        </div>
      )}

      {!loading && !error && total === 0 && (
        <div className="bg-cream border border-line rounded-lg p-6 text-ink-soft" data-testid="review-empty">
          {emptyMessage}
        </div>
      )}

      {!loading && !error && current && (
        <>
          {/* Metadata bar — full-width Number (ID) locator (FR-012), Scoring (red), and the
              read-only assigned Difficulty (N/A when unassigned). Matches the 013 page's bar. */}
          <div className="mb-4 short:mb-3">
            <div
              className="bg-card border border-line rounded-lg p-2 short:p-1.5 flex flex-wrap gap-4 short:gap-3 text-sm short:text-xs"
              data-testid="review-meta"
            >
              <span data-testid="review-locator">
                <span className="text-ink-soft">Number (ID):</span>{' '}
                <span className="font-semibold text-ink">{current.id}</span>
              </span>
              <span className="text-red-600 font-bold" data-testid="review-scoring">
                <span>Scoring:</span> <span>{current.scoring || 'N/A'}</span>
              </span>
              <span data-testid="review-difficulty">
                <span className="text-ink-soft">Difficulty:</span>{' '}
                <span className="font-semibold text-ink">
                  {current.difficultyLevel != null ? `${current.difficultyLevel}/10` : 'N/A'}
                </span>
              </span>
            </div>
          </div>

          {/* Main content — same gray panel + problem-items layout as the 013 page */}
          <div className="bg-cream rounded-lg p-4 short:p-3 narrow:p-2 mb-3 short:mb-1 mobile-flex-col">
            <div className="flex flex-col flex-1 min-h-0 narrow:justify-start narrow:items-center lphone-outer">
              <div className="flex flex-wrap flex-1 min-h-0 gap-3 short:gap-2 narrow:gap-1.5 justify-center items-stretch narrow:flex-col narrow:flex-nowrap narrow:items-center narrow:justify-start lphone-items problem-items">
                {/* Vulnerability board + You Hold — placement follows the hand's seat (data-seat) */}
                <div
                  className="flex gap-3 short:gap-2 narrow:gap-1.5 items-center flex-shrink-0 vuln-hand-group"
                  data-seat={current.decisionSeat}
                >
                  <div className="flex-shrink-0">
                    <VulnerabilityIndicator
                      vulnerability={current.vulnerability}
                      vulnerabilityText={current.vulnerabilityText}
                      decisionSeat={current.decisionSeat}
                      dealer={current.auction[0]?.seat}
                    />
                  </div>
                  <div className="bg-card border border-line rounded-lg p-2 short:p-1.5 flex-shrink-0 w-[clamp(9rem,40vw,11rem)] md:w-[clamp(7rem,16.5vw,9rem)] lg:w-[clamp(8rem,18vw,12rem)] lphone-youhold">
                    <h2 className="font-semibold text-ink short:h-[1.75rem] short:text-[0.95rem]" style={{ fontSize: '1rem' }}>
                      You hold{' '}
                      <span className="text-ink-soft font-normal">
                        (<span className="lg:hidden">{current.decisionSeat}</span>
                        <span className="hidden lg:inline">{DIRECTION_NAMES[current.decisionSeat]}</span>)
                      </span>
                    </h2>
                    <HandDisplay hand={current.playerHand} />
                  </div>
                </div>

                {/* The masked auction */}
                <div className="bg-card border border-line rounded-lg p-2 short:p-1.5 flex flex-col auction-container-width">
                  <h2 className="font-semibold text-ink mb-1 short:mb-0.5 auction-header" style={{ fontSize: '1rem' }}>
                    The Auction
                  </h2>
                  <div className="auction-scroll scrollable flex-1">
                    <AuctionTable
                      auction={current.auction}
                      vulnerability={current.vulnerability}
                      decisionSeat={current.decisionSeat}
                    />
                  </div>
                </div>

                {/* Result/feedback slot. In "Hands with comments" it shows the combined
                    "Answer:" / "Jeff says:" container (FR-011c); otherwise Jeff's bid + Jim's
                    answer (read-only). */}
                <div className="feedback-container-width self-stretch narrow:flex-auto narrow:self-auto">
                  {category === 'hands-with-comments' ? (
                    <ThirdLevelCommentResult
                      jeffBid={current.jeffBid ?? ''}
                      jeffComment={current.jeffComment ?? ''}
                    />
                  ) : (
                    <ThirdLevelReviewResult
                      jeffBid={current.jeffBid ?? ''}
                      jimAnswer={current.isAnswered ? current.editorAnswer : null}
                      jimAnswerCorrect={current.editorAnswerCorrect}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Action row: Previous / Next on the LEFT (where Skip Problem sits on the 013 page) */}
          <div className="flex flex-wrap gap-3 short:gap-2 mt-auto flex-shrink-0">
            <button
              type="button"
              onClick={goPrev}
              disabled={atFirst}
              className="px-5 py-2.5 short:px-3 short:py-1 short:text-sm rounded-md font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:bg-paper disabled:text-ink-soft disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={goNext}
              disabled={atLast}
              className="px-5 py-2.5 short:px-3 short:py-1 short:text-sm rounded-md font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:bg-paper disabled:text-ink-soft disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}
